/**
 * Camada de acesso a dados do Compasso (repositórios). Leituras pensadas para
 * `useLiveQuery`; escritas respeitam as regras da spec: origem gravada sempre,
 * momentos da criança nascem pendentes (confirmado=false).
 *
 * Nota sobre `confirmado`: o IndexedDB não indexa booleanos, então filtramos
 * pendências em memória (a base local é pequena).
 */
import { db } from "../schema.dexie";
import { momentoDaCrianca } from "./domain";
import type {
  ContextoMomento,
  EstadoRegulacao,
  ExperienciaMusical,
  FuncaoMusical,
  Modalidade,
  MomentoMusical,
  PassoEngajamento,
  PerfilSonoro,
  StatusExperiencia,
  TipoExperiencia,
} from "./types";

const novoId = () => crypto.randomUUID();
const agoraISO = () => new Date().toISOString();

const SONORO_VAZIO: PerfilSonoro = {
  andamento: null,
  intensidade: null,
  timbre: null,
  previsibilidade: null,
  familiaridade: null,
};

// ── Leituras (use com useLiveQuery) ─────────────────────────────────────────

/** Experiências não arquivadas, ordenadas por nome. */
export const listarExperiencias = (incluirArquivadas = false): Promise<ExperienciaMusical[]> =>
  db.experiencia
    .orderBy("nome")
    .filter((e) => incluirArquivadas || !e.arquivado)
    .toArray();

export const getExperiencia = (id: string) => db.experiencia.get(id);

export const getTodosMomentos = (): Promise<MomentoMusical[]> => db.momento.toArray();

export const getMomentosDaExperiencia = (experienciaId: string): Promise<MomentoMusical[]> =>
  db.momento
    .where("[experiencia_id+data_hora]")
    .between([experienciaId, ""], [experienciaId, "￿"])
    .reverse()
    .toArray();

/** Momentos pendentes de confirmação (origem criança). Filtra em memória. */
export const getMomentosPendentes = (): Promise<MomentoMusical[]> =>
  db.momento.filter((m) => m.confirmado === false).toArray();

// ── Biblioteca (P0.1) ────────────────────────────────────────────────────────

export type NovaExperienciaInput = {
  nome: string;
  tipo: TipoExperiencia;
  funcao?: FuncaoMusical | null;
  sonoro?: Partial<PerfilSonoro>;
  modalidade?: Modalidade[];
  status?: StatusExperiencia;
  observacoes?: string | null;
  midias?: string[];
};

export async function criarExperiencia(input: NovaExperienciaInput): Promise<string> {
  const exp: ExperienciaMusical = {
    id: novoId(),
    nome: input.nome.trim(),
    tipo: input.tipo,
    midias: input.midias ?? [],
    funcao: input.funcao ?? null,
    sonoro: { ...SONORO_VAZIO, ...input.sonoro },
    modalidade: input.modalidade ?? [],
    status: input.status ?? "nao_usada",
    observacoes: input.observacoes ?? null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.experiencia.add(exp);
  return exp.id;
}

export const editarExperiencia = (id: string, patch: Partial<ExperienciaMusical>) =>
  db.experiencia.update(id, patch);

export const definirStatusExperiencia = (id: string, status: StatusExperiencia) =>
  db.experiencia.update(id, { status });

/** P0.1 — arquivar em vez de deletar. */
export const arquivarExperiencia = (id: string) =>
  db.experiencia.update(id, { arquivado: true });

// ── Momentos (P0.2 / P0.3) ────────────────────────────────────────────────────

export type NovoMomentoInput = {
  experiencia_id: string;
  crianca_id: string;
  passo_engajamento: PassoEngajamento;
  estado_antes?: EstadoRegulacao | null;
  estado_depois?: EstadoRegulacao | null;
  duracao_min?: number | null;
  contexto?: ContextoMomento | null;
  com_quem?: string[];
  notas?: string | null;
};

/** P0.2 — registro do cuidador: origem fixa, confirmado, em ≤10s. */
export async function registrarMomentoCuidador(input: NovoMomentoInput): Promise<string> {
  const momento: MomentoMusical = {
    id: novoId(),
    data_hora: agoraISO(),
    estado_antes: null,
    estado_depois: null,
    duracao_min: null,
    contexto: null,
    notas: null,
    ...input,
    com_quem: input.com_quem ?? [],
    origem: "cuidador",
    confirmado: true,
  };
  await db.momento.add(momento);
  return momento.id;
}

/** P0.3 — escolha da criança: origem `crianca`, nasce pendente (confirmado=false). */
export async function registrarEscolhaCrianca(
  experienciaId: string,
  criancaId: string,
  passo: PassoEngajamento,
  contexto: ContextoMomento | null = null,
): Promise<string> {
  const base = {
    id: novoId(),
    experiencia_id: experienciaId,
    crianca_id: criancaId,
    data_hora: agoraISO(),
    passo_engajamento: passo,
    estado_antes: null,
    estado_depois: null,
    duracao_min: null,
    contexto,
    com_quem: [] as string[],
    notas: "escolheu no modo escolha",
  };
  const momento = momentoDaCrianca(base);
  await db.momento.add(momento);
  return momento.id;
}

export const confirmarMomento = (id: string) =>
  db.momento.update(id, { confirmado: true });

/** Confirma uma escolha da criança e ajusta o passo de engajamento observado. */
export const confirmarMomentoComPasso = (id: string, passo: PassoEngajamento) =>
  db.momento.update(id, { confirmado: true, passo_engajamento: passo });

export const descartarMomento = (id: string) => db.momento.delete(id);
